/**
 * Internal Messaging — timestamp helpers for the thread view.
 *
 * Distinct from the global <c>relativeTime</c> formatter (used by the
 * notification bell + conversation list) because the messaging UI wants a
 * tighter, opinionated style that matches mainstream chat apps.
 *
 * Every function expects an ISO 8601 string from the API plus the user's
 * IANA timezone (`MeDto.timeZone`) so the visible day boundaries and
 * clock matches the rest of the UI.
 */

import { FALLBACK_TIMEZONE } from "@/utils/datetime";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Compact label for a message bubble:
 *   < 60s    → "just now"
 *   < 1h     → "5m ago"
 *   same day → "3:42 PM"
 *   < 7d     → "Yesterday at 3:42 PM" / "Monday at 3:42 PM"
 *   older    → "Apr 24, 3:42 PM"
 */
export function formatMessageTime(iso: string, tz: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const diffMs = now.getTime() - d.getTime();

  if (diffMs < 60_000) return "just now";
  if (diffMs < HOUR) {
    const m = Math.max(1, Math.floor(diffMs / MINUTE));
    return `${m}m ago`;
  }

  if (isSameDayInTz(d, now, tz)) {
    return formatClock(d, tz);
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (isSameDayInTz(d, yesterday, tz)) {
    return `Yesterday at ${formatClock(d, tz)}`;
  }

  if (diffMs < 7 * DAY) {
    const weekday = d.toLocaleDateString("en-US", { weekday: "long", timeZone: tz || FALLBACK_TIMEZONE });
    return `${weekday} at ${formatClock(d, tz)}`;
  }

  // Older than a week — use a short date that still includes the year if
  // the message is from a previous calendar year, otherwise omit the year
  // to keep the chip compact.
  const showYear = yearInTz(d, tz) !== yearInTz(now, tz);
  return d.toLocaleDateString("en-US", {
    timeZone: tz || FALLBACK_TIMEZONE,
    month: "short",
    day: "numeric",
    year: showYear ? "numeric" : undefined,
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Full datetime in the user's tz, suitable for a `title` attribute on every
 * timestamp. E.g., "April 25, 2026 at 3:42 PM PDT".
 */
export function formatFullLocalDateTime(iso: string, tz: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    timeZone: tz || FALLBACK_TIMEZONE,
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

/**
 * Date-separator label that walks the thread chronologically. Returns
 * "Today" / "Yesterday" / weekday name / "Mar 14, 2026" — all computed
 * against the user's tz, never UTC, so the visible group matches the
 * user's wall clock.
 */
export function formatDateSeparator(iso: string, tz: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (isSameDayInTz(d, now, tz)) return "Today";

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (isSameDayInTz(d, yesterday, tz)) return "Yesterday";

  const dayMs = 86_400_000;
  // Approximate week boundary in ms — fine for the < 7 day check.
  const diffMs = now.getTime() - d.getTime();
  if (diffMs > 0 && diffMs < 7 * dayMs) {
    return d.toLocaleDateString("en-US", { weekday: "long", timeZone: tz || FALLBACK_TIMEZONE });
  }

  const showYear = yearInTz(d, tz) !== yearInTz(now, tz);
  return d.toLocaleDateString("en-US", {
    timeZone: tz || FALLBACK_TIMEZONE,
    month: "short",
    day: "numeric",
    year: showYear ? "numeric" : undefined,
  });
}

function formatClock(d: Date, tz: string): string {
  return d.toLocaleTimeString("en-US", {
    timeZone: tz || FALLBACK_TIMEZONE,
    hour: "numeric",
    minute: "2-digit",
  });
}

function dateKeyInTz(d: Date, tz: string): string {
  return d.toLocaleDateString("en-CA", {
    timeZone: tz || FALLBACK_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function isSameDayInTz(a: Date, b: Date, tz: string): boolean {
  return dateKeyInTz(a, tz) === dateKeyInTz(b, tz);
}

function yearInTz(d: Date, tz: string): string {
  return d.toLocaleDateString("en-US", {
    timeZone: tz || FALLBACK_TIMEZONE,
    year: "numeric",
  });
}
